/* eslint-disable max-len */
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import * as React from 'react';

import { useCallback, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import uuid from 'react-uuid';
import { getAnswerListStyle, getItemStyle } from '../../../../utils';
import { useTableDataContext } from '../../../context/EditingTableDataContext';
import { columns } from '../../../utils';
import { ActionsCell } from './ActionsCell';
import { ThirdLayer } from './ThirdLayer';

export const SecondLayer = ({
  firstLayerItemNum,
  firstLayerIndex,
  secondLayerItemNum,
  secondLayerDescription,
  secondLayerUnit,
  secondLayerQuantity,
  secondLayerUnitPrice,
  secondLayerPrice,
  secondLayerFinalized,
  secondLayerIndex,
}) => {
  const { allTableDataWithExtraInfo, updateTableData, setToaster } =
    useTableDataContext();

  const [cellEditable, setCellEditable] = useState({
    description: '',
    unit: '',
    quantity: '',
    unitPrice: '',
    isFinalized: '',
  });

  React.useEffect(() => {
    setCellEditable({
      description: secondLayerDescription,
      unit: secondLayerUnit,
      quantity: secondLayerQuantity,
      unitPrice: secondLayerUnitPrice,
      isFinalized: secondLayerFinalized,
    });
  }, [
    secondLayerDescription,
    secondLayerQuantity,
    secondLayerUnit,
    secondLayerUnitPrice,
    secondLayerFinalized,
  ]);

  const updateSecondLayerFieldsHandler = React.useCallback(
    (e) => {
      const newState = [...allTableDataWithExtraInfo.items_attributes];

      const { name, value, checked } = e.target;

      let valueToFormat = value;

      if (name === 'unit_price') {
        const numericValue = value.replace(/\D/g, '').replace(',', '.');
        const floatValue = parseFloat(numericValue || 0) / 100;
        valueToFormat = floatValue;
      }

      if (name === 'quantity') {
        valueToFormat = value.replace(/[^0-9.,]/g, '');
      }

      newState[firstLayerIndex].items_attributes[secondLayerIndex][name] =
        name === 'is_finalized' ? checked : valueToFormat;

      const quantitySecondLayer =
        newState[firstLayerIndex].items_attributes[secondLayerIndex].quantity ||
        '0';
      const unitPriceSecondLayer =
        newState[firstLayerIndex].items_attributes[secondLayerIndex]
          .unit_price || 0;

      if (
        (name === 'quantity' || name === 'unit_price') &&
        (quantitySecondLayer !== '' ||
          quantitySecondLayer !== '0' ||
          quantitySecondLayer ||
          quantitySecondLayer !== 0) &&
        unitPriceSecondLayer
      ) {
        const totalPrice =
          typeof quantitySecondLayer === 'number'
            ? quantitySecondLayer * unitPriceSecondLayer
            : Number(quantitySecondLayer.replace(',', '.')) *
              unitPriceSecondLayer;

        newState[firstLayerIndex].items_attributes[secondLayerIndex].price =
          totalPrice;
        let firstLayerPrice = 0;

        newState[firstLayerIndex].items_attributes.forEach(
          (secondLayerItem) => {
            firstLayerPrice += secondLayerItem.price || 0;
          }
        );

        newState[firstLayerIndex].price = firstLayerPrice;
      }

      if (
        (name === 'quantity' || name === 'unit_price') &&
        (quantitySecondLayer === '' ||
          quantitySecondLayer === '0' ||
          !quantitySecondLayer ||
          !unitPriceSecondLayer)
      ) {
        newState[firstLayerIndex].price -=
          newState[firstLayerIndex].items_attributes[secondLayerIndex].price;
        newState[firstLayerIndex].items_attributes[secondLayerIndex].price = 0;
      }

      updateTableData({
        ...allTableDataWithExtraInfo,
        items_attributes: newState,
      });
    },
    [
      allTableDataWithExtraInfo,
      firstLayerIndex,
      secondLayerIndex,
      updateTableData,
    ]
  );

  const currentSecondLayerArrayData = React.useMemo(
    () =>
      allTableDataWithExtraInfo.items_attributes[firstLayerIndex]
        .items_attributes[secondLayerIndex],
    [
      allTableDataWithExtraInfo.items_attributes,
      firstLayerIndex,
      secondLayerIndex,
    ]
  );

  const hasSubitems = !!currentSecondLayerArrayData.items_attributes.length;
  const canHaveSubItems =
    !cellEditable.unitPrice && !cellEditable.quantity && !cellEditable.unit;

  const generateTableCell = ({ editable, align, data, id, propertyName }) => {
    if (
      hasSubitems &&
      (id === 'unit' ||
        id === 'quantity' ||
        id === 'unit_price' ||
        id === 'is_finalized')
    ) {
      return <TableCell />;
    }

    if (id === 'is_finalized') {
      return (
        <TableCell align={align}>
          <Checkbox
            sx={{ m: 'auto' }}
            name={id}
            checked={data}
            onChange={(e) => {
              setCellEditable((prevState) => ({
                ...prevState,
                [propertyName]: e.target.checked,
              }));
              updateSecondLayerFieldsHandler(e);
            }}
            color="default"
          />
        </TableCell>
      );
    }

    return (
      <TableCell
        align={align}
        sx={{ cursor: hasSubitems && !editable ? 'pointer' : 'initial' }}
      >
        {editable ? (
          <input
            name={id}
            style={{
              outline: 0,
              border: '0 none',
              fontFamily: 'montserrat',
              background: 'none',
              width: '100%',
              textAlign: align,
              fontSize: '12px',
            }}
            placeholder="Insira o dado"
            value={
              id === 'unit_price'
                ? data.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })
                : data
            }
            onChange={(e) => {
              setCellEditable((prevState) => ({
                ...prevState,
                [propertyName]: e.target.value,
              }));
              updateSecondLayerFieldsHandler(e);
            }}
          />
        ) : (
          <span style={{ fontFamily: 'montserrat', fontSize: '12px' }}>
            {id === 'price'
              ? data.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })
              : data}
          </span>
        )}
      </TableCell>
    );
  };

  const deleteRowFromCurrentLayer = useCallback(() => {
    const selectedItem =
      allTableDataWithExtraInfo.items_attributes[firstLayerIndex]
        .items_attributes[secondLayerIndex];

    const subItemsHavePayables = selectedItem.items_attributes.some(
      (subItem) => subItem.has_payables
    );

    if (selectedItem.has_payables || subItemsHavePayables) {
      setToaster({
        open: true,
        msg: 'Este item possui lançamentos associados e não pode ser excluído. Edite os lançamentos para remover a associação antes de excluir este item.',
      });
      setTimeout(() => setToaster({ open: false, msg: '' }), 5000);
    } else {
      const newArrayOfRows = allTableDataWithExtraInfo.items_attributes.map(
        (firstLayerItem, L1index) => {
          if (L1index === firstLayerIndex) {
            const updatedSecondLayerItems = firstLayerItem.items_attributes
              .filter((_item, index) => index !== secondLayerIndex)
              .map((item, index) => ({
                ...item,
                item_num: `${index + 1}`,
              }));

            let newFirstLayerPrice = 0;
            updatedSecondLayerItems.forEach((item) => {
              newFirstLayerPrice += item.price;
            });

            return {
              ...firstLayerItem,
              price: newFirstLayerPrice,
              items_attributes: updatedSecondLayerItems,
            };
          }
          return firstLayerItem;
        }
      );
      updateTableData({
        ...allTableDataWithExtraInfo,
        items_attributes: newArrayOfRows,
      });
    }
  }, [
    allTableDataWithExtraInfo,
    firstLayerIndex,
    secondLayerIndex,
    updateTableData,
  ]);

  const addNewRowThirdLayer = useCallback(() => {
    const newState = { ...allTableDataWithExtraInfo };

    newState.items_attributes[firstLayerIndex].items_attributes[
      secondLayerIndex
    ].items_attributes.push({
      item_num: `${currentSecondLayerArrayData.items_attributes.length + 1}`,
      description: '',
      unity: '',
      quantity: '',
      unit_price: '',
      price: '',
      items_attributes: [],
      id: null,
    });

    updateTableData(newState);
  }, [
    allTableDataWithExtraInfo,
    currentSecondLayerArrayData.items_attributes.length,
    firstLayerIndex,
    secondLayerIndex,
    updateTableData,
  ]);

  return (
    <Draggable
      index={secondLayerIndex}
      draggableId={`${firstLayerItemNum}-${secondLayerItemNum}`}
    >
      {/* eslint-disable-next-line no-shadow */}
      {(provided, snapshot) => (
        <>
          <TableRow
            className="secondary-item"
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(
              snapshot.isDragging,
              provided.draggableProps.style
            )}
          >
            <TableCell align="left" />
            {generateTableCell({
              data: `${firstLayerItemNum}.${secondLayerItemNum}`,
              align: 'left',
              id: 'item_num',
            })}
            {generateTableCell({
              data: cellEditable.description,
              align: 'left',
              editable: true,
              id: 'description',
              propertyName: 'description',
            })}
            {generateTableCell({
              data: cellEditable.unit,
              align: 'center',
              editable: !hasSubitems,
              id: 'unit',
              propertyName: 'unit',
            })}
            {generateTableCell({
              data: cellEditable.quantity,
              align: 'center',
              type: 'number',
              editable: !hasSubitems,
              id: 'quantity',
              propertyName: 'quantity',
            })}
            {generateTableCell({
              data: cellEditable.unitPrice,
              align: 'center',
              type: 'number',
              editable: !hasSubitems,
              id: 'unit_price',
              propertyName: 'unitPrice',
            })}
            {generateTableCell({
              data: secondLayerPrice,
              align: 'center',
              id: 'price',
            })}
            {generateTableCell({
              data: cellEditable.isFinalized,
              align: 'center',
              id: 'is_finalized',
            })}
            <ActionsCell
              canHaveSubItems={canHaveSubItems}
              addNewItemToSubLayer={addNewRowThirdLayer}
              provided={provided}
              deleteRowFromCurrentLayer={deleteRowFromCurrentLayer}
              addNewItemSubLayerIconStyle={{
                cursor: hasSubitems ? 'pointer' : 'default',
              }}
              dragIconStyle={{
                cursor: 'inherit',
                width: '20px',
                marginTop: '5px',
              }}
              deleteIconStyle={{
                cursor: 'pointer',
                color: 'red',
              }}
              iconSize="small"
            />
          </TableRow>

          {/* THIRD LAYER / THIRD TABLE */}
          {hasSubitems && (
            <Droppable
              droppableId={`droppable-${firstLayerItemNum}-${firstLayerIndex}`}
              type={`${firstLayerIndex}-${secondLayerIndex}`}
            >
              {/* eslint-disable-next-line no-shadow */}
              {(provided, snapshot) => (
                <TableRow
                  ref={provided.innerRef}
                  style={getAnswerListStyle(snapshot.isDraggingOver)}
                  className="tertiary-item"
                >
                  <TableCell style={{ padding: 0 }} align="center" colSpan={12}>
                    <Box sx={{ margin: 1 }}>
                      <Typography variant="h6" gutterBottom component="div" />
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '25px' }} />
                            {columns.map((column) => (
                              <TableCell
                                key={uuid()}
                                align={column.align}
                                sx={{
                                  width: column.width,
                                  fontSize: '12px',
                                  fontWeight: 700,
                                }}
                              >
                                {column.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {!!currentSecondLayerArrayData.items_attributes
                            .length &&
                            currentSecondLayerArrayData.items_attributes.map(
                              (item, index) => (
                                <ThirdLayer
                                  key={item.item_num}
                                  firstLayerIndex={firstLayerIndex}
                                  firstLayerItemNum={firstLayerItemNum}
                                  secondLayerIndex={secondLayerIndex}
                                  secondLayerItemNum={secondLayerItemNum}
                                  thirdLayerItemNum={item.item_num}
                                  thirdLayerDescription={item.description}
                                  thirdLayerUnit={item.unit}
                                  thirdLayerQuantity={item.quantity}
                                  thirdLayerUnitPrice={item.unit_price}
                                  thirdLayerPrice={item.price}
                                  thirdLayerFinalized={item.is_finalized}
                                  thirdLayerIndex={index}
                                />
                              )
                            )}
                          {provided.placeholder}
                        </TableBody>
                      </Table>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </Droppable>
          )}
        </>
      )}
    </Draggable>
  );
};

SecondLayer.propTypes = {
  firstLayerItemNum: PropTypes.string.isRequired,
  firstLayerIndex: PropTypes.number.isRequired,
  secondLayerItemNum: PropTypes.string.isRequired,
  secondLayerDescription: PropTypes.string.isRequired,
  secondLayerUnit: PropTypes.string.isRequired,
  secondLayerQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  secondLayerUnitPrice: PropTypes.number.isRequired,
  secondLayerPrice: PropTypes.number.isRequired,
  secondLayerFinalized: PropTypes.bool.isRequired,
  secondLayerIndex: PropTypes.number.isRequired,
};
